import type { DataRetentionRow, LegalDocument } from '@/features/legal/tipos';

/**
 * Legal texts in English — **courtesy translations**.
 *
 * The Spanish versions in `locales/es/legal.ts` are the binding ones: they were
 * drafted in Spanish, they cite Mexican law (LFPDPPP), and they are what
 * contracts and the Google Play listing point at. `LegalDocumentPage` shows a
 * notice to that effect on every non-Spanish rendering, with a link back to the
 * original — see `avisoTraduccion`.
 *
 * Practical consequence to keep in mind: a Google Play reviewer opening
 * `/barbunny/eliminar-cuenta` almost certainly has an English browser, so
 * **this** is the text they will read. It has to stay complete and current, not
 * a stub.
 *
 * ⚠️ Pending a legal read. Translated alongside the i18n work; no lawyer has
 * reviewed the English wording.
 */

export const legal = {
  avisoTraduccion:
    'This is a courtesy translation. The Spanish version is the original and the only binding one; if the two differ, the Spanish text prevails.',
  verEnEspanol: 'Read the Spanish version',
  volverAlInicio: 'Back to home',

  meta: {
    privacidad:
      'ILUMETRIA Privacy Notice under Mexico’s LFPDPPP: what data we capture at in-person events and how to exercise your ARCO rights.',
    terminos:
      'ILUMETRIA Terms of Service for the use of its platform and data intelligence hardware at events.',
    cookies: 'ILUMETRIA Cookie Policy, covering digital and passive tracking.',
    barbunnyPrivacidad:
      'Privacy Notice for Bar Bunny, the loyalty app operated by Ilumetria.',
    barbunnyTerminos:
      'Terms and Conditions for the Bar Bunny loyalty app, operated by Ilumetria.',
  },

  ilumetria: {
    privacidad: {
      title: 'Privacy Notice',
      effectiveNote: 'Last updated: 28 August 2026',
      intro: [
        'Issued under Mexico’s Federal Law on the Protection of Personal Data Held by Private Parties (LFPDPPP).',
        'At ILUMETRIA, the privacy of our users and of event attendees is our priority. This notice describes how we capture and protect information in physical environments through our data intelligence technology, and how we handle the data of people who take part in our industry studies.',
      ],
      sections: [
        {
          title: '1. Identity and Responsibility',
          paragraphs: [
            'ILUMETRIA is the controller of the data collected, and ensures that passive tracking technologies are used under the highest standards of security and anonymization.',
          ],
        },
        {
          title: '2. Data We Collect',
          paragraphs: [
            'Our technology captures movement flows, dwell times and general professional profiles. We do not collect sensitive personal information without explicit consent.',
          ],
        },
        {
          title: '3. Industry studies',
          paragraphs: [
            'When you take part in an ILUMETRIA industry study — such as our study on how events are measured in Mexico today — we collect your email address at the start and, at the end, your name and your organization’s name, together with the answers you choose to give us.',
            'We process this data for three primary purposes: running and analysing the study, saving your progress so you can pick it up if you leave halfway, and sending you the results report once it is ready.',
            'Results are published in aggregate. No answer is quoted with your name or your organization’s name without your express authorization.',
          ],
        },
        {
          title: '4. Use of your contact details for commercial purposes',
          paragraphs: [
            'At the end of the study we ask you, in a separate checkbox that is unticked by default, whether you authorize us to contact you. That checkbox is an additional purpose and is not required in order to take part: if you leave it unticked, your study is still saved and your answers still count.',
            'If you tick it, your name, email, organization and the state you told us are added to our industry contact directory, which we use for commercial follow-up — among other things, to offer you the free measurement pilot if you expressed interest in it. That directory is for ILUMETRIA’s internal use: it is not sold, rented or transferred to third parties.',
            'You may withdraw this authorization at any time, without affecting the lawfulness of processing carried out beforehand, through the same channel provided for ARCO rights. On withdrawal, your contact details are removed from the directory and your answers remain in the study in dissociated form.',
          ],
        },
        {
          title: '5. Your ARCO Rights',
          paragraphs: [
            'You may exercise your rights of Access, Rectification, Cancellation and Objection by writing to hola@ilumetria.com.',
          ],
        },
      ],
    } satisfies LegalDocument,

    terminos: {
      title: 'Terms of Service',
      effectiveNote: 'In force for the United Mexican States — February 2026',
      intro: [],
      sections: [
        {
          title: '1. Acceptance of Terms',
          paragraphs: [
            'By using the ILUMETRIA platform or services, users agree to be bound by these Terms and Conditions, which are governed by the civil and commercial laws of Mexico.',
          ],
        },
        {
          title: '2. Use of the Technology',
          paragraphs: [
            'The client undertakes to use ILUMETRIA hardware and software solely for the agreed commercial purposes, respecting the brand’s intellectual property.',
          ],
        },
        {
          title: '3. Limited Liability',
          paragraphs: [
            'ILUMETRIA provides intelligence based on statistical data. We are not liable for commercial decisions taken by the user, nor for force majeure events affecting on-site data collection.',
          ],
        },
        {
          title: '4. Jurisdiction',
          paragraphs: [
            'For any dispute, the parties submit to the jurisdiction of the competent courts of Mexico City, waiving any other venue.',
          ],
        },
      ],
    } satisfies LegalDocument,

    cookies: {
      title: 'Cookie Policy',
      effectiveNote: 'Digital and Passive Tracking Policy',
      intro: [],
      sections: [
        {
          title: 'What are cookies and similar technologies?',
          paragraphs: [
            'On our website we use cookies to improve your experience. In our physical services, we use electronic tags that work much like a "physical cookie" to understand a visitor’s journey.',
          ],
        },
        {
          title: 'Types of cookies we use',
          paragraphs: [
            'Essential: required for the dashboard to work.',
            'Analytics: they help us understand traffic on the site and at the physical event.',
            'Personalization: they let us remember your data display preferences.',
          ],
        },
        {
          title: 'Controlling your privacy',
          paragraphs: [
            'You can set your browser to reject our web cookies; some features of the intelligence system may be limited as a result.',
          ],
        },
      ],
    } satisfies LegalDocument,
  },

  barbunny: {
    privacidad: {
      title: 'Privacy Notice — Bar Bunny',
      effectiveNote: 'Applies to the Bar Bunny loyalty app, operated by Ilumetria.',
      intro: [],
      sections: [
        {
          title: '1. Data Controller',
          paragraphs: [
            'Last updated: August 2026. Bar Bunny S.A. de C.V. is the entity responsible for processing the personal data obtained through the Cuponera Bar Bunny loyalty application, available on the web, Google Play and the App Store.',
          ],
        },
        {
          title: '2. Data We Collect',
          paragraphs: [
            'Contact and identification data: name, phone number, email address and date of birth. Profile data (optional): gender, area, occupation, favorite drink/music/day and preferred time. Loyalty card data: Bunny Card ID and linked NFC card. Program activity: visit history, points, stamps and rewards redeemed. We do not request or store official IDs, payment details or real-time location.',
          ],
        },
        {
          title: '3. Purpose of Data Use',
          paragraphs: [
            'Your data is used solely to create and manage your account, assign you benefits, manage your stamp card, prevent duplication or fraud, verify your mobile number and personalize your experience on each visit to Bar Bunny.',
          ],
        },
        {
          title: '4. Who We Share Your Data With',
          paragraphs: [
            'We do not sell or share your personal data with advertisers or with third parties for commercial purposes, and we do not carry out advertising tracking across apps or websites. Your data is processed only by our own team and by the cloud infrastructure providers that run the service (hosting and database) under confidentiality obligations. Links to WhatsApp, Instagram and Facebook take you to third-party services governed by their own privacy policies.',
          ],
        },
        {
          title: '5. Where Your Data Is Stored',
          paragraphs: [
            'Your information is held on cloud servers with encryption in transit (HTTPS/TLS). Your session and, in Demo Mode, your loyalty profile, are also stored locally in your browser or device storage (localStorage) to keep you signed in between visits.',
          ],
        },
        {
          title: '6. Use by Minors',
          paragraphs: [
            'The Bar Bunny loyalty program is intended solely for people aged 18 and over. We do not knowingly collect personal data from minors; if we identify a minor’s account, it will be deleted.',
          ],
        },
        {
          title: '7. Retention Period',
          paragraphs: [
            'We keep your data for as long as your account remains active. Points expire after 12 months of inactivity counted from your last visit. If you delete your account, your personal data is permanently erased from our systems, unless the law requires us to keep certain information for a further period.',
          ],
        },
        {
          title: '8. Your Rights and Deleting Your Account',
          paragraphs: [
            'You may request access, rectification, cancellation or objection (ARCO rights) regarding the processing of your data by writing to privacidad@barbunny.com or in person at the Bar Bunny till. You can also delete your account and all your data at any time, without contacting us, from Settings → Delete my Account inside the app; this action is immediate and irreversible.',
          ],
        },
        {
          title: '9. Security of Your Information',
          paragraphs: [
            'We apply reasonable technical and organizational measures to protect your data against unauthorized access, loss or alteration, including encrypted communication (HTTPS) between the app and our servers.',
          ],
        },
        {
          title: '10. Changes to This Notice',
          paragraphs: [
            'We may update this Privacy Notice from time to time to reflect changes in the service or in applicable regulations. The "Last updated" date at the top of this document indicates the version in force.',
          ],
        },
        {
          title: '11. Contact',
          paragraphs: [
            'For questions about this Privacy Notice or the processing of your data, write to us at privacidad@barbunny.com.',
          ],
        },
      ],
    } satisfies LegalDocument,

    terminos: {
      title: 'Terms and Conditions — Bar Bunny',
      effectiveNote: 'Applies to the Bar Bunny loyalty app, operated by Ilumetria.',
      intro: [],
      sections: [
        {
          title: '1. Acceptance of the Terms',
          paragraphs: [
            'Last updated: July 2026. By registering, signing in or using your NFC/digital card on the Cuponera Bar Bunny platform, you fully accept these Terms and Conditions of Use.',
          ],
        },
        {
          title: '2. Age Requirement (18+)',
          paragraphs: [
            'The loyalty and consumption program at Bar Bunny requires the user to be at least 18 years old and to present official photo identification (INE/Passport) at the request of staff.',
          ],
        },
        {
          title: '3. Points and Rewards System',
          paragraphs: [
            'Purchases and check-ins earn points and stamps under the program rules in force, calculated and validated by the loyalty system. Points expire after 12 months of inactivity counted from your last visit.',
          ],
        },
        {
          title: '4. Service Restrictions',
          paragraphs: [
            'Accumulated points and stamps cannot be exchanged for cash or monetary transfer. Bar Bunny reserves the right to void points or cancel memberships in the event of fraudulent use.',
          ],
        },
      ],
    } satisfies LegalDocument,
  },

  eliminarCuenta: {
    titulo: 'Delete your Bar Bunny account',
    descripcion:
      'How to delete your account and personal data from Bar Bunny, the loyalty app operated by Ilumetria — from the app or by email.',
    intro: (entidad: string) =>
      `This page applies to Bar Bunny, the loyalty app operated by ${entidad}. It explains how to delete your account and what happens to your data when you do.`,

    desdeLaApp: 'From the app',
    pasos: [
      'Open the Bar Bunny app on your phone and sign in to your account.',
      'On the main screen ("Mi Cuponera"), tap the gear icon ⚙️ (Settings) in the top right corner.',
      'Inside Settings, scroll to the bottom and tap "Eliminar Cuenta Definitivamente" (Delete Account Permanently).',
      'Confirm on the warning message. The deletion is processed immediately.',
    ],
    inmediata:
      'This action is immediate and irreversible — there is no need to wait for anyone to process it.',

    sinApp: 'If you no longer have the app',
    sinAppAsunto: 'Delete my Bar Bunny account',
    sinAppTexto: (correo: string) =>
      `Write to us at ${correo} requesting the deletion of your account. Include the phone number you registered with — that is how we identify your account.`,

    queSeElimina: 'What data is deleted',
    tabla: {
      seElimina: 'Deleted',
      seConserva: 'Retained',
      porQue: 'Why',
    },

    plazo: 'Timing',
    plazoTexto: 'Deletion from the app: immediate and irreversible.',
    plazoCorreoSinSla:
      'Email requests are handled as soon as we can confirm the account is yours.',

    slaCorreo: null as string | null,
    notaConservacionLegal: null as string | null,

    filas: [
      {
        id: 'nombre',
        deleted: 'Name (replaced with "Cliente eliminado")',
        retained: '—',
        reason: 'N/A',
      },
      {
        id: 'telefono',
        deleted: 'Phone number (replaced with a non-reusable value)',
        retained: '—',
        reason: 'N/A',
      },
      {
        id: 'identificacion',
        deleted: 'Email address, date of birth, gender, area, occupation, Instagram handle',
        retained: '—',
        reason: 'N/A',
      },
      {
        id: 'verificacion',
        deleted: 'Identity verification status and photo/URL',
        retained: '—',
        reason: 'N/A',
      },
      {
        id: 'contrasena',
        deleted:
          'Password (replaced with a random, unusable value — the account can no longer sign in)',
        retained: '—',
        reason: 'N/A',
      },
      {
        id: 'preferencias',
        deleted:
          'Profile preferences (favorite drink, music, day and time, reason for visiting) — deleted entirely',
        retained: '—',
        reason: 'N/A',
      },
      {
        id: 'historial',
        deleted: '—',
        retained:
          'Visit, coupon and points history (linked to an already anonymized identifier, not to your real name or phone number)',
        reason:
          'Accounting and operational integrity of the loyalty program (aggregate branch reporting); the system itself treats this history as a record that is never edited or deleted, tied to an id rather than to your already deleted personal data',
      },
    ] satisfies DataRetentionRow[],
  },
};
